/* Vesper Cocktails redesign: product page behaviour (Product.dc.html).
   - [data-vc-product]  buy box: gallery, variant options, packs, quantity, add to cart
   - [data-vc-faq]      single-open accordion (first item open, like the mockup)
   - [data-vc-reco]     swaps the fallback grid for Shopify "related" recommendations
   - [data-vc-review-cta] scrolls to the Judge.me widget and opens its review form
   Loaded as a module by the vc-product-* sections; idempotent per element. */

let Events = null;
import('@theme/events').then((m) => { Events = m; }).catch(() => {});

const cartRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function formatMoney(cents, format) {
  cents = Number(cents) || 0;
  format = format || '${{amount}}';
  const re = /\{\{\s*(\w+)\s*\}\}/;
  const num = (value, precision = 2, thousands = ',', decimal = '.') => {
    const fixed = (value / 100).toFixed(precision);
    const [d, f] = fixed.split('.');
    return d.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (f ? decimal + f : '');
  };
  const m = format.match(re);
  let v;
  switch (m ? m[1] : 'amount') {
    case 'amount_no_decimals': v = num(cents, 0); break;
    case 'amount_with_comma_separator': v = num(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': v = num(cents, 0, '.', ''); break;
    case 'amount_with_apostrophe_separator': v = num(cents, 2, "'", '.'); break;
    case 'amount_with_space_separator': v = num(cents, 2, ' ', ','); break;
    default: v = num(cents, 2);
  }
  return format.replace(re, v);
}

/* ---------- Buy box ---------- */
function initProduct(root) {
  if (root.__vcInit) return;
  root.__vcInit = true;

  const money = (c) => formatMoney(c, root.dataset.moneyFormat);
  let variants = [];
  try { variants = JSON.parse(root.querySelector('[data-vc-variants]').textContent); } catch (_) {}
  const form = root.querySelector('[data-vc-form]');
  if (!form) return;
  const idInput = form.querySelector('[data-vc-variant-id]');
  const qtyInput = form.querySelector('[data-vc-qty-input]');
  const qtyOut = root.querySelector('[data-vc-qty]');
  const atc = root.querySelector('[data-vc-add]');
  const atcLabel = root.querySelector('[data-vc-add-label]');
  const priceEl = root.querySelector('[data-vc-price]');
  const compareEl = root.querySelector('[data-vc-compare]');
  const packs = Array.from(root.querySelectorAll('[data-vc-pack]'));
  const optionGroups = Array.from(root.querySelectorAll('[data-vc-option]'));
  const slides = Array.from(root.querySelectorAll('[data-vc-slide]'));
  const thumbs = Array.from(root.querySelectorAll('[data-vc-thumb]'));

  const state = {
    variant: variants.find((x) => String(x.id) === String(idInput.value)) || variants[0] || null,
    pack: Math.max(0, packs.findIndex((p) => p.classList.contains('is-on'))),
    qty: 1,
    added: false,
    busy: false,
  };

  const tierQty = (i) => (packs[i] ? Math.max(1, parseInt(packs[i].dataset.qty, 10) || 1) : 1);
  const tierOff = (i) => (packs[i] ? Math.max(0, parseInt(packs[i].dataset.off, 10) || 0) : 0);
  const tierPrice = (i) => {
    const unit = state.variant ? state.variant.price : 0;
    return Math.max(0, unit * tierQty(i) - tierOff(i));
  };

  function showMedia(mediaId) {
    if (!mediaId || !slides.length) return;
    let found = false;
    slides.forEach((s) => {
      const on = s.dataset.mediaId === String(mediaId);
      if (on) found = true;
      s.classList.toggle('is-on', on);
      if (on) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
      if (!on) s.querySelectorAll('video').forEach((vid) => { try { vid.pause(); } catch (_) {} });
    });
    if (!found) return;
    thumbs.forEach((t) => {
      const on = t.dataset.mediaId === String(mediaId);
      t.classList.toggle('is-on', on);
      if (on) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
    });
  }

  function render() {
    const v = state.variant;
    const available = !!(v && v.available);
    packs.forEach((p, i) => {
      const on = i === state.pack;
      p.classList.toggle('is-on', on);
      p.setAttribute('aria-pressed', String(on));
      const pe = p.querySelector('[data-vc-pack-price]');
      if (pe) pe.textContent = money(tierPrice(i));
    });
    if (v && priceEl) priceEl.textContent = money(v.price);
    if (compareEl) {
      const sale = v && v.compare_at_price && v.compare_at_price > v.price;
      compareEl.hidden = !sale;
      if (sale) compareEl.textContent = money(v.compare_at_price);
    }
    if (qtyOut) qtyOut.textContent = String(state.qty);
    if (qtyInput) qtyInput.value = String(state.qty * tierQty(state.pack));
    if (idInput && v) idInput.value = String(v.id);
    optionGroups.forEach((g) => {
      const idx = parseInt(g.dataset.vcOption, 10);
      g.querySelectorAll('[data-vc-option-value]').forEach((b) => {
        const on = v && v.options[idx] === b.dataset.vcOptionValue;
        b.classList.toggle('is-on', !!on);
        b.setAttribute('aria-pressed', String(!!on));
        const sel = v ? v.options.slice() : [];
        sel[idx] = b.dataset.vcOptionValue;
        const match = variants.find((x) => x.options.every((o, k) => o === sel[k]));
        b.classList.toggle('is-unavailable', !match || !match.available);
      });
    });
    if (!atc || state.busy) return;
    atc.disabled = !available;
    if (!available) atcLabel.textContent = atc.dataset.soldOutLabel || 'Sold Out';
    else if (state.added) atcLabel.textContent = atc.dataset.addedLabel || 'Added To Cart';
    else {
      atcLabel.textContent = atc.dataset.addLabel || 'Add To Cart';
      const price = document.createElement('span');
      price.className = 'vc-product-hero__atc-price';
      price.textContent = ' · ' + money(tierPrice(state.pack) * state.qty);
      atcLabel.appendChild(price);
    }
  }

  function selectOption(idx, value) {
    const sel = state.variant ? state.variant.options.slice() : [];
    sel[idx] = value;
    const exact = variants.find((x) => x.options.every((o, k) => o === sel[k]));
    const next = exact || variants.find((x) => x.options[idx] === value && x.available) || variants.find((x) => x.options[idx] === value);
    if (!next) return;
    state.variant = next;
    state.added = false;
    if (next.featured_media) showMedia(next.featured_media.id);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', next.id);
      window.history.replaceState({}, '', url.toString());
    } catch (_) {}
    render();
  }

  root.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const pack = t.closest('[data-vc-pack]');
    if (pack) { state.pack = packs.indexOf(pack); state.added = false; render(); return; }
    if (t.closest('[data-vc-inc]')) { state.qty += 1; state.added = false; render(); return; }
    if (t.closest('[data-vc-dec]')) { state.qty = Math.max(1, state.qty - 1); state.added = false; render(); return; }
    const opt = t.closest('[data-vc-option-value]');
    if (opt) { selectOption(parseInt(opt.closest('[data-vc-option]').dataset.vcOption, 10), opt.dataset.vcOptionValue); return; }
    const thumb = t.closest('[data-vc-thumb]');
    if (thumb) showMedia(thumb.dataset.mediaId);
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    add();
  });

  async function add() {
    if (!atc || atc.disabled || state.busy) return;
    state.busy = true;
    atc.setAttribute('aria-busy', 'true');
    const original = atcLabel.textContent;
    const body = new FormData(form);
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    if (sectionIds.length) body.append('sections', sectionIds.join(','));
    const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || cartRoot() + 'cart/add.js';
    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        body,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add');
      state.added = true;

      let cartCount;
      try {
        const c = await (await fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        cartCount = c.item_count;
      } catch (_) {}
      const id = String(state.variant ? state.variant.id : body.get('id'));
      const data = { source: 'vc-product-main', itemCount: cartCount, cartCount, productId: root.dataset.productId, variantId: id, sections: json.sections };
      const evt = Events && Events.CartAddEvent
        ? new Events.CartAddEvent({}, id, data)
        : Object.assign(new Event('cart:update', { bubbles: true }), { detail: { resource: {}, sourceId: id, data } });
      document.dispatchEvent(evt);
      if (typeof cartCount === 'number') {
        document.querySelectorAll('[data-vc-cart-count]').forEach((el) => { el.textContent = String(cartCount); });
      }
    } catch (err) {
      atcLabel.textContent = (err && err.message) ? err.message : 'Unavailable';
      setTimeout(() => { atcLabel.textContent = original; render(); }, 2600);
      state.busy = false;
      atc.removeAttribute('aria-busy');
      return;
    }
    state.busy = false;
    atc.removeAttribute('aria-busy');
    render();
  }

  render();
}

/* ---------- FAQ accordion ---------- */
function initFaq(root) {
  if (root.__vcInit) return;
  root.__vcInit = true;
  const items = Array.from(root.querySelectorAll('[data-vc-faq-item]'));
  const set = (open) => items.forEach((it, i) => {
    const on = i === open;
    it.classList.toggle('is-open', on);
    const b = it.querySelector('[data-vc-faq-toggle]');
    const a = it.querySelector('[data-vc-faq-answer]');
    if (b) b.setAttribute('aria-expanded', String(on));
    if (a) a.hidden = !on;
    const sign = it.querySelector('[data-vc-faq-sign]');
    if (sign) sign.textContent = on ? '–' : '+';
  });
  root.addEventListener('click', (e) => {
    const b = e.target instanceof Element ? e.target.closest('[data-vc-faq-toggle]') : null;
    if (!b) return;
    const i = items.indexOf(b.closest('[data-vc-faq-item]'));
    set(items[i].classList.contains('is-open') ? -1 : i);
  });
}

/* ---------- Review CTA -> Judge.me ---------- */
document.addEventListener('click', (e) => {
  const cta = e.target instanceof Element ? e.target.closest('[data-vc-review-cta]') : null;
  if (!cta) return;
  const scope = cta.closest('[data-vc-reviews-wrap]') || document;
  const widget = scope.querySelector('.jdgm-widget, .jdgm-rev-widg, [data-vc-reviews]');
  if (!widget) return;
  e.preventDefault();
  widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
  if (cta.dataset.vcReviewCta === 'write') {
    const link = widget.querySelector('.jdgm-write-rev-link');
    if (link) setTimeout(() => link.click(), 400);
  }
});

/* ---------- Related products ---------- */
async function initReco(root) {
  if (root.__vcInit) return;
  root.__vcInit = true;
  const url = root.dataset.url;
  const grid = root.querySelector('[data-vc-reco-grid]');
  if (!url || !grid) return;
  try {
    const html = await (await fetch(url)).text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-vc-reco-grid]');
    const min = parseInt(root.dataset.min, 10) || 2;
    if (!fresh || fresh.children.length < min) return;
    grid.innerHTML = fresh.innerHTML;
    grid.querySelectorAll('[data-vc-seen]').forEach((el) => el.removeAttribute('data-vc-seen'));
    grid.removeAttribute('data-vc-seen');
    if (window.VCMotion) { window.VCMotion.scan(); }
  } catch (_) { /* keep fallback */ }
}

function initAll() {
  document.querySelectorAll('[data-vc-product]').forEach(initProduct);
  document.querySelectorAll('[data-vc-faq]').forEach(initFaq);
  document.querySelectorAll('[data-vc-reco]').forEach(initReco);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
else initAll();
document.addEventListener('shopify:section:load', initAll);
